import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../services/api";
import { useAuth } from "./AuthContext";

const ProfileContext = createContext(null);

export function ProfileProvider({ children }) {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await api.get("/profiles");
      setProfiles(data);
      const savedId = localStorage.getItem("pq_profile");
      const found = data.find((p) => p.id === savedId);
      if (found) setCurrent(found);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) refresh();
    else {
      setProfiles([]);
      setCurrent(null);
    }
  }, [user, refresh]);

  const createProfile = async (payload) => {
    const { data } = await api.post("/profiles", payload);
    setProfiles((prev) => [...prev, data]);
    return data;
  };

  const deleteProfile = async (id) => {
    await api.delete(`/profiles/${id}`);
    setProfiles((prev) => prev.filter((p) => p.id !== id));
    if (current?.id === id) {
      setCurrent(null);
      localStorage.removeItem("pq_profile");
    }
  };

  const selectProfile = (p) => {
    setCurrent(p);
    localStorage.setItem("pq_profile", p.id);
  };

  const clearProfile = () => {
    setCurrent(null);
    localStorage.removeItem("pq_profile");
  };

  const saveProgress = async (levelId, stars, accuracy, skillScores = {}) => {
    if (!current) return;
    const { data } = await api.put(`/profiles/${current.id}/progress`, {
      level_id: levelId,
      stars,
      accuracy,
      skill_scores: skillScores,
    });
    setCurrent(data);
    setProfiles((prev) => prev.map((p) => (p.id === data.id ? data : p)));
    return data;
  };

  const saveCave = async (chapterId, caveId, stars, reward = null) => {
    if (!current) return;
    const { data } = await api.put(`/profiles/${current.id}/cave`, {
      chapter_id: chapterId,
      cave_id: caveId,
      stars,
      reward,
    });
    setCurrent(data);
    setProfiles((prev) => prev.map((p) => (p.id === data.id ? data : p)));
    return data;
  };

  const updateSettings = async (id, age_band) => {
    const { data } = await api.put(`/profiles/${id}/settings`, { age_band });
    setProfiles((prev) => prev.map((p) => (p.id === id ? data : p)));
    if (current?.id === id) setCurrent(data);
    return data;
  };

  return (
    <ProfileContext.Provider
      value={{ profiles, current, loading, refresh, createProfile, deleteProfile, selectProfile, clearProfile, saveProgress, saveCave, updateSettings }}
    >
      {children}
    </ProfileContext.Provider>
  );
}

export const useProfile = () => useContext(ProfileContext);
